import { PASTE_PROFILE, sanitizeHtml } from "@norkron/web-docz";
import { utf8ToBase64, type BriefFormat } from "./fileIngest";

/**
 * FOUR ways in → THREE server formats → ONE ingest function.
 *
 * The owner's four paths ("upload the brief either in docx or md format or just
 * copy and paste the text to the editor or write it directly in the editor")
 * collapse onto `src/seed/render.zig:renderBytes(arena, format, bytes, title)`,
 * whose `format` is exactly `"md" | "docx" | "html"`. This module is the PURE
 * mapping — no React, no fetch — so every path is assertable on its content.
 *
 *   1. upload .docx  → format "docx", the file's bytes
 *   2. upload .md    → format "md",   the file's bytes
 *   3. paste         → format "html" when the clipboard carried rich HTML,
 *                      else format "md" over the RAW text
 *   4. write here    → format "",     no bytes (a legal empty brief)
 *
 * ── WHY PASTE IS "md" AND NOT PRE-ESCAPED (a shipped bug, do not undo) ───────
 * `src/seed/render.zig:196-212` records the regression: text was HTML-escaped
 * before being handed to markdownz, and docz escapes ONCE MORE at emit time, so
 * `1 < 2` reached the reader as a literal `1 &lt; 2`. markdownz has no raw-HTML
 * production — an injected `<script>` arrives at docz as run TEXT and is escaped
 * exactly once on the way out. So the plain-text paste is sent VERBATIM. Any
 * "be safe, escape it first" instinct here reintroduces that exact defect.
 *
 * ── WHY RICH PASTE IS SANITIZED CLIENT-SIDE ANYWAY ──────────────────────────
 * The HTML branch runs `sanitizeHtml(html, PASTE_PROFILE)` — the SAME filter
 * `web-docz`'s `parseClipboard` already applies inside the editor, so a paste
 * into the create dialog and a paste into the editor keep the same subset of
 * formatting (RULE ZERO: never a second sanitizer). This is a UX filter. The
 * TRUST BOUNDARY is `src/ingest/html_sanitize.zig` on the server, which
 * re-sanitizes regardless; the client's output is never trusted.
 */

/** What the user actually supplied, before it is lowered to the wire. */
export type BriefSource =
  | {
      readonly mode: "file";
      readonly filename: string;
      readonly format: BriefFormat;
      /** base64 of the raw file bytes. */
      readonly contentB64: string;
    }
  | {
      readonly mode: "paste";
      /** `text/html` flavour of the clipboard, when the source had one. */
      readonly html?: string | null;
      /** `text/plain` flavour — always present for a paste. */
      readonly text: string;
    }
  | { readonly mode: "editor" };

/**
 * The three ingest fields of the create request, named EXACTLY as the server's
 * `handlers/article_units.zig:CreateBody` declares them — the SPA carries the
 * server's names end to end rather than translating at the socket, because a
 * translation layer is precisely where the shipped seam hid: this shape used to
 * be `format` / `filename` / `content_b64`, `reply.parseBody` dropped all three
 * without a word, and every created project got an EMPTY brief.
 * `brief_format: ""` + empty `brief_content_b64` is the explicit "start empty,
 * type it in the editor" case.
 */
export interface BriefWire {
  readonly brief_format: BriefFormat | "";
  readonly brief_filename: string;
  readonly brief_content_b64: string;
}

/**
 * Tags a browser emits merely to hold LINES, carrying no formatting. Pasting
 * plain text into a `contentEditable` produces `<div>line</div><div>line</div>`
 * or `line<br>line` — html-shaped, but with nothing in it that markdown could
 * not express better.
 */
const PLAIN_WRAPPERS: ReadonlySet<string> = new Set(["div", "p", "br", "span"]);

/**
 * Does this sanitized html carry real FORMATTING (a heading, a mark, a list, a
 * table, a link)? — as opposed to line-wrapping chrome.
 *
 * This is the fork between the two paste paths, and getting it wrong is
 * user-visible either way: send a pasted markdown brief down the html path and
 * its `# Heading` arrives as literal text; send a Word paste down the markdown
 * path and every bold, link and table is flattened away.
 */
function hasRichFormatting(sanitized: string): boolean {
  const tags = sanitized.match(/<([a-z][a-z0-9]*)\b/gi) ?? [];
  return tags.some((t) => !PLAIN_WRAPPERS.has(t.slice(1).toLowerCase()));
}

/**
 * Lower a {@link BriefSource} onto the create request's ingest fields.
 *
 * Pure and total: every input yields a wire, and an empty paste degrades to the
 * same shape as mode 4 rather than posting an empty `md` document.
 */
export function briefWire(source: BriefSource): BriefWire {
  if (source.mode === "file") {
    return {
      brief_format: source.format,
      brief_filename: source.filename,
      brief_content_b64: source.contentB64,
    };
  }

  if (source.mode === "paste") {
    const rich = source.html ? sanitizeHtml(source.html, PASTE_PROFILE) : "";
    if (rich && hasRichFormatting(rich)) {
      return {
        brief_format: "html",
        brief_filename: "pasted.html",
        brief_content_b64: utf8ToBase64(rich),
      };
    }
    // Plain text → markdown. VERBATIM (see the header note on double-escaping).
    const text = source.text;
    if (text.trim().length === 0) return EMPTY_BRIEF;
    return {
      brief_format: "md",
      brief_filename: "pasted.md",
      brief_content_b64: utf8ToBase64(text),
    };
  }

  return EMPTY_BRIEF;
}

/** The "no bytes" wire — a brand-new project whose brief is typed in the editor. */
const EMPTY_BRIEF: BriefWire = {
  brief_format: "",
  brief_filename: "",
  brief_content_b64: "",
};

/** Does this wire actually carry a brief? (`false` ⇒ the project is created with
 *  an empty brief and the user is sent to the editor to write it.) */
export function carriesBrief(wire: BriefWire): boolean {
  return wire.brief_content_b64.length > 0;
}

// ── reading the paste surface ───────────────────────────────────────────────

/**
 * Read what actually landed in the paste box, as the MARKDOWN this module sends.
 *
 * UPSTREAM (HARD RULE 13): the walk itself is generic and now lives in
 * `@norkron/web-docz` beside `parseClipboard`/`sanitizeHtml`
 * (`domBlockText`/`domMarkdownText`) — this is a named re-export so the create
 * dialog reads the surface through ONE function whose name states the
 * destination.
 *
 * ── WHY THE MARKDOWN READING AND NOT THE PLAIN ONE (the shipped bug) ────────
 * The plain reading puts ONE `\n` at a block boundary. The wire format here is
 * `"md"`, where a single newline is a SOFT break — so `First.\nSecond.` reaches
 * the reader as the single paragraph "First. Second.". Every multi-paragraph
 * pasted brief was silently flattened into one paragraph between the clipboard
 * and the document. `domMarkdownText` separates each boundary by a blank line,
 * which is the only thing Markdown reads as a paragraph break.
 */
export { domMarkdownText as domPasteText } from "@norkron/web-docz";
